import { Purchases } from "@revenuecat/purchases-js";

import { REVENUECAT_API_KEYS } from "~/lib/constants";

import {
  purchaseErrorMessage,
  type PurchaseCustomerInfo,
  type PurchasePackage,
  type PurchaseService,
  inferPurchasePlan,
} from "./purchases";
import { FLORAFOLIO_PRO_ENTITLEMENT } from "./revenuecat";

let purchases: Purchases | null = null;
let configuredUserId: string | null = null;

function billingPeriodFor(packageType: unknown) {
  const value = String(packageType).toLowerCase();
  if (value.includes("annual")) return "annual";
  if (value.includes("monthly")) return "monthly";
  return "other";
}

function toCustomerInfo(
  info: Awaited<ReturnType<Purchases["getCustomerInfo"]>>,
): PurchaseCustomerInfo {
  const activeEntitlements = Object.keys(info.entitlements.active);
  return {
    activeEntitlements,
    hasPro: Boolean(info.entitlements.active[FLORAFOLIO_PRO_ENTITLEMENT]),
    managementUrl: info.managementURL,
    originalAppUserId: info.originalAppUserId ?? null,
  };
}

async function ensureConfigured(userId: string) {
  const apiKey = REVENUECAT_API_KEYS.web;
  if (!apiKey) {
    throw new Error("RevenueCat web API key is missing from source constants.");
  }
  if (!purchases) {
    purchases = Purchases.configure({ apiKey, appUserId: userId });
    configuredUserId = userId;
    return;
  }
  if (configuredUserId !== userId) {
    await purchases.changeUser(userId);
    configuredUserId = userId;
  }
}

export const purchaseService: PurchaseService = {
  supportsNativePaywall: false,
  supportsCustomerCenter: false,

  async load(userId) {
    const apiKey = REVENUECAT_API_KEYS.web;
    if (!apiKey) {
      return {
        configured: false,
        packages: [],
        customerInfo: {
          activeEntitlements: [],
          hasPro: false,
          managementUrl: null,
          originalAppUserId: null,
        },
      };
    }
    await ensureConfigured(userId);
    if (!purchases) throw new Error("Purchases are not configured.");
    const [offerings, customerInfo] = await Promise.all([
      purchases.getOfferings(),
      purchases.getCustomerInfo(),
    ]);
    const packages = (offerings.current?.availablePackages ?? []).map((pkg) => {
      const title = pkg.webBillingProduct.title;
      return {
        identifier: pkg.identifier,
        title,
        plan: inferPurchasePlan(
          pkg.identifier,
          title,
          pkg.webBillingProduct.identifier,
        ),
        priceText: pkg.webBillingProduct.currentPrice.formattedPrice,
        billingPeriod: billingPeriodFor(pkg.packageType),
        raw: pkg,
      } satisfies PurchasePackage;
    });
    return {
      configured: true,
      packages,
      customerInfo: toCustomerInfo(customerInfo),
    };
  },

  async purchase(pkg) {
    if (!purchases) throw new Error("Purchases are not configured.");
    try {
      const { customerInfo } = await purchases.purchase({
        rcPackage: pkg.raw as never,
      });
      return toCustomerInfo(customerInfo);
    } catch (error) {
      throw new Error(
        purchaseErrorMessage(error, "Unable to complete purchase.") ??
          "Unable to complete purchase.",
      );
    }
  },

  async restore() {
    if (!purchases) throw new Error("Purchases are not configured.");
    const customerInfo = await purchases.getCustomerInfo();
    return toCustomerInfo(customerInfo);
  },

  async getCustomerInfo() {
    if (!purchases) throw new Error("Purchases are not configured.");
    const customerInfo = await purchases.getCustomerInfo();
    return toCustomerInfo(customerInfo);
  },

  async getManagementUrl() {
    if (!purchases) throw new Error("Purchases are not configured.");
    const customerInfo = await purchases.getCustomerInfo();
    return customerInfo.managementURL;
  },

  async presentPaywall() {
    throw new Error(
      "RevenueCat Paywalls UI is only available on iOS and Android.",
    );
  },

  async presentPaywallIfNeeded() {
    throw new Error(
      "RevenueCat Paywalls UI is only available on iOS and Android.",
    );
  },

  async presentCustomerCenter() {
    throw new Error(
      "RevenueCat Customer Center is only available on iOS and Android.",
    );
  },

  addCustomerInfoListener() {
    return () => undefined;
  },
};
